import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import Typography from '@mui/material/Typography';
import IconLargeCicle from '../../shared/IconLargeCicle';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useCancelScheduleByIdWithTokenMutation,
  useConfirScheduleByIdMutation,
  useVerifyActiveScheduleByIdMutation,
  VerifyActiveScheduleDTO
} from '../../../services/schedule.service';
import { formatDateLabel } from '../../../lib/dayjs';
import ExpiredConfirmationSchedule from '../ExpiredConfirmationSchedule';
import Button from '@mui/material/Button';
import { useIsLoggedIn } from '../../../shared/hooks';
import { setRedirect } from '../../../features/redirect';
import { useDispatch } from 'react-redux';
import { dynamicCustomEventTracking } from '../../../lib/gtm';
import { ButtonWrapper, DoctorRoot, DoctorRootPhoto, DoctorRootTitle } from './styled';
import Link from '@mui/material/Link';
import { getHost } from '../../../lib/global';

export default function ConfirmationSignalScheduleRoot() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLogged = useIsLoggedIn();
  const [isLoading, setIdLoading] = useState(true);
  const [isExpire, seIsExpire] = useState(true);
  const { scheduleId } = useParams();
  const [verifyActiveScheduleData, setVerifyActiveScheduleData] = useState<VerifyActiveScheduleDTO>();
  const [verifyActiveScheduleById] = useVerifyActiveScheduleByIdMutation();
  const [cancelScheduleByIdWithToken, { isLoading: isLoadingCancelJourney }] = useCancelScheduleByIdWithTokenMutation();
  const [confirScheduleById, { data: confirmScheduleData }] = useConfirScheduleByIdMutation();

  const handleReschedule = () => {
    if (isLogged) {
      dynamicCustomEventTracking.send({
        dynamicEventName: 'signal_reschedule_logged_mc'
      });
      navigate(`/jornada/${verifyActiveScheduleData?.journey.id}`);
    } else {
      dynamicCustomEventTracking.send({
        dynamicEventName: 'signal_reschedule_logged_mc'
      });
      dispatch(setRedirect(`/jornada/${verifyActiveScheduleData?.journey.id}`));
      navigate(`/login`);
    }
  };

  const handleCancelSchedule = () => {
    cancelScheduleByIdWithToken({ id: verifyActiveScheduleData?.id || '', token: verifyActiveScheduleData?.token || '' })
      .unwrap()
      .then(res => {
        if (!res) {
          seIsExpire(true);
          return;
        }
        dynamicCustomEventTracking.send({
          dynamicEventName: 'signal_cancel_schedule_mc'
        });
        navigate(`/motivo-do-cancelamento/${verifyActiveScheduleData?.id}`, {
          state: { token: verifyActiveScheduleData?.token }
        });
      })
      .catch(() => {
        seIsExpire(true);
      });
  };

  useEffect(() => {
    verifyActiveScheduleById({ id: scheduleId || '' })
      .unwrap()
      .then(res => {
        if (!res) {
          seIsExpire(true);
          setIdLoading(false);
          return;
        }
        setIdLoading(false);
        seIsExpire(false);
        setVerifyActiveScheduleData(res);
      })
      .catch(() => {
        seIsExpire(true);
        setIdLoading(false);
      });
  }, []);

  useEffect(() => {
    if (verifyActiveScheduleData) {
      confirScheduleById({ id: verifyActiveScheduleData.id || '', token: verifyActiveScheduleData.token || '' });
    }
  }, [verifyActiveScheduleData]);

  useEffect(() => {
    if (confirmScheduleData) {
      seIsExpire(false);
      setIdLoading(false);
      dynamicCustomEventTracking.send({ dynamicEventName: 'confirmation_schedule_mc' });
    }
  }, [confirmScheduleData]);

  if (isLoading || isLoadingCancelJourney) {
    return <CircularProgress sx={{ color: 'brand.darkGreen' }} />;
  }

  if (isExpire) {
    return <ExpiredConfirmationSchedule />;
  }

  return (
    <>
      <IconLargeCicle>
        <CheckIcon />
      </IconLargeCicle>

      <Typography variant="h2" sx={{ mb: 3 }}>
        Sua consulta está confirmada!
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Obrigada pela confirmação. Estamos te aguardando dia {formatDateLabel(verifyActiveScheduleData?.dateStart || '')}!
      </Typography>
      <DoctorRoot sx={{ mb: 2 }}>
        <DoctorRootTitle>
          <Typography variant="body1B">Quem vai atender</Typography>
          <Typography variant="body1">Dra. {confirmScheduleData?.journey.user.name}</Typography>
        </DoctorRootTitle>
        <DoctorRootPhoto>
          <img
            src={confirmScheduleData?.journey.user.profileImage || 'https://placehold.co/100x100'}
            alt="Foto do(a) médico(a)"
            width={60}
            height={60}
          />
        </DoctorRootPhoto>
      </DoctorRoot>

      <Typography variant="body1" sx={{ mb: 5 }}>
        Ao realizar exames para o seu atendimento, lembre-se de nos enviar os resultados. É só entrar no seu{' '}
        <Link href={getHost()} target="_blank" sx={{ textDecoration: '' }}>
          Login na Oya
        </Link>{' '}
        e anexar os documentos.
      </Typography>
      <ButtonWrapper>
        <Button variant="contained" color="secondary" onClick={() => handleCancelSchedule()}>
          Cancelar consulta
        </Button>
        <Button variant="contained" color="primary" onClick={() => handleReschedule()}>
          Reagendar
        </Button>
      </ButtonWrapper>
    </>
  );
}
